/** @flow */

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import config from "@config";
import {
  GetHierarchyChangelog,
  ExportHierarchyChangelog,
  ExportHierarchyChangelogSuccess,
  GetHierarchyChangelogUsers,
} from "@modules/hierarchy/constants/actions";
import actionCreators from "@modules/hierarchy/actionCreator";
import {
  getHierarchyChangelogPage,
  getHierarchyChangelogFilters,
} from "@modules/hierarchy/selectors/change-log";
import { transformHierarchyChangelogFilters } from "@modules/hierarchy/transformers/change-log";
import { downloadCsvFile } from "@sagas/utils";

type ChangelogRequest = $Exact<{
  clientId: string,
  page?: number,
}>;

export function* onGetHierarchyChangelog({
  payload,
}: Action<ChangelogRequest>): Generator<*, void, Object> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  const currentPage = yield select(getHierarchyChangelogPage);
  const page = (payload && payload.page) || currentPage;
  const filters = yield select(getHierarchyChangelogFilters);

  const transformedFilters = filters && transformHierarchyChangelogFilters(filters);

  try {
    const response = yield call(
      http.get,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/log`,
      {
        params: {
          search: transformedFilters,
          page,
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetHierarchyChangelogSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetHierarchyChangelogError(e.response));
  }
}

export function* onExportHierarchyChangelog({
  payload,
}: Action<ChangelogRequest>): Generator<*, void, Object> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/log/export`,
      {
        headers: {
          Accept: "text/csv",
        },
      },
    );

    yield put(actionCreators.ExportHierarchyChangelogSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.ExportHierarchyChangelogError(e.response));
  }
}

export function* onExportHierarchyChangelogSuccess({
  payload,
}: Action<any>): Generator<*, void, any> {
  yield downloadCsvFile(payload, "hierarchy-change-log.csv");
}

export function* onGetHierarchyChangelogUsers({
  payload,
}: Action<ChangelogRequest>): Generator<*, void, Object> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/log/users`,
    );

    yield put(actionCreators.GetHierarchyChangelogUsersSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetHierarchyChangelogUsersError(e.response));
  }
}

export function* HierarchyChangelogSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetHierarchyChangelog, onGetHierarchyChangelog),
    takeEvery(ExportHierarchyChangelog, onExportHierarchyChangelog),
    takeEvery(ExportHierarchyChangelogSuccess, onExportHierarchyChangelogSuccess),
    takeEvery(GetHierarchyChangelogUsers, onGetHierarchyChangelogUsers),
  ]);
}

export default HierarchyChangelogSagas;
